'use client'

import { useState, type ComponentProps } from 'react'

import { openCheckout } from '@/lib/paddle/checkout'
import AeoResults from './components/AeoResults'
import { useToolRunOrCheckout } from '@/hooks/useToolRunOrCheckout'

import ToolAeoPageAuditorRef from '@/components/reference/ToolAeoPageAuditorRef'
import { useUrlRun } from '@/components/reference/tool-run'
import {
  ToolLiveError,
  ToolLiveStatus,
  ToolPayWall,
} from '@/components/reference/tool-live'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'

export default function AeoAuditorClient() {
  const [label, setLabel] = useState('')
  // The exact URL submitted, frozen so editing the input after the wall appears
  // cannot change what the $5 checkout pays to run.
  const [submittedUrl, setSubmittedUrl] = useState('')
  // Owner/paid → full result inline (free); everyone else → 402 → the $5 wall.
  const {
    run,
    loading,
    error: runError,
    result,
    walled,
  } = useToolRunOrCheckout<ComponentProps<typeof AeoResults>['result']>(
    '/api/tools/aeo-auditor'
  )

  // Start path: freeze the submitted URL, run (owner/paid inline, everyone
  // else hits the $5 wall), scroll to output. Input validation lives in
  // ToolEntryHero.
  function startAudit(trimmed: string) {
    setLabel(trimmed.replace(/^https?:\/\//, ''))
    setSubmittedUrl(trimmed)
    void run({ url: trimmed })
    setTimeout(() => {
      document
        .getElementById('aeo-output')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  const refRun = useUrlRun({
    example: 'pixelmojo.io/blogs',
    isLoading: loading,
    onSubmit: startAudit,
  })

  return (
    <div className='shell pg-tool pg-tool-aeo-page-auditor'>
      <ToolAeoPageAuditorRef
        run={refRun}
        output={
          <>
            {(loading || runError || result || walled) && (
              <div id='aeo-output' className='max-w-4xl mx-auto mb-12'>
                {loading && (
                  <ToolLiveStatus tool='AEO Page Auditor'>
                    Auditing {label}…
                  </ToolLiveStatus>
                )}

                {runError && !loading && (
                  <ToolLiveError>{runError}</ToolLiveError>
                )}

                {result && !loading && <AeoResults result={result} />}

                {walled && !loading && (
                  <ToolPayWall
                    tool='AEO Page Auditor'
                    title={<>Run the AEO audit for {label}</>}
                    onCheckout={() =>
                      openCheckout('pack-single', 'tool_teaser', {
                        source: 'tool_run',
                        toolSlug: 'aeo-auditor',
                        url: submittedUrl,
                      })
                    }
                  >
                    Pay a one-time $5 to score <strong>{label}</strong> across
                    all 6 AEO categories: every finding and the priority fixes.
                    Your report opens right here.
                  </ToolPayWall>
                )}
              </div>
            )}
          </>
        }
      />
    </div>
  )
}
